import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, getDuration, Text } from '@common-fe/common-fe';

import { useForgotPassword } from '@/modules/user/components/SignIn/ForgotPassword/hooks';
import useResendCodeTimer from '@/modules/user/components/SignIn/hooks/useResendCodeTimer';
import { useResendCodeTimerStore } from '@/modules/user/components/SignIn/stores/useResendCodeTimer.store';
import { USERNAME_QUERY_KEY } from '@/modules/user/components/SignUp/signUp.constants';
import { useQueryParams } from '@/utils/hooks';
import { convertSecondsToMinutes } from '@/utils/modifiers/convertSecondsToMinutes';

import VerificationBackground from './components/VerificationBackground';
import VerificationMessage from './components/VerificationMessage';

const ForgotPasswordLinkSent = () => {
  const { t } = useTranslation();
  const query = useQueryParams();
  const username = useMemo(() => query.get(USERNAME_QUERY_KEY), [query]);
  const { handleRequestCode } = useForgotPassword();
  const { setTimer } = useResendCodeTimerStore();
  const { timeLeft } = useResendCodeTimer();

  const resendActivation = useCallback(() => {
    if (username && !timeLeft) {
      handleRequestCode(username);
      setTimer({ expiredIn: `${Date.now() + getDuration({ minutes: 1 })}` });
    }
  }, [timeLeft, username, handleRequestCode, setTimer]);

  return (
    <Box
      direction="column"
      align="center"
      justify="end"
      background="background2"
      height={{
        min: '100vh',
      }}
    >
      <VerificationMessage
        title="Reset link sent!"
        resendButtonTitle={
          <>
            {t('Resend')} {timeLeft ? `(${convertSecondsToMinutes(timeLeft)})` : null}
          </>
        }
        disableResend={!!timeLeft}
        onResend={resendActivation}
        footerText="If you received a reset email, you can close this page."
        subTitle={
          <Text textAlign="center" size="large" color="textOnColor" style={{ lineHeight: 1.4 }}>
            {t('We\'ve sent a reset link to the email address on file.')}
          </Text>
        }
      />
      <VerificationBackground />
    </Box>
  );
};

export default ForgotPasswordLinkSent;
